// src/lib/specSheet.js
// ───────────────────────────────────────────────────────────────────────────
// Spec Sheet (PDF) — "hoja de imposición" para el DISEÑADOR (PLAN-MOTOR-PRECIO-MVP F2 §6.2).
// Reusa la geometría que ya calculó el motor (functions/lib/dielines): blank size,
// rule length (cut/crease), ups y nesting. Se genera del lado cliente (jsPDF) a partir
// del estimado/cotización; NO recalcula nada (el servidor es la fuente de verdad).
// ───────────────────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Carga el logo como dataURL (best-effort: si falla, el PDF se genera sin él).
async function loadLogo() {
  try {
    const url = `${process.env.PUBLIC_URL || ""}/cartonbar-logo.png`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch (_) { return null; }
}

const n = (v, d = 2) => (typeof v === "number" ? v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d }) : "—");

// bbox de un set de polilíneas (in).
function bboxOf(polys) {
  let minx = Infinity; let miny = Infinity; let maxx = -Infinity; let maxy = -Infinity;
  for (const { pts } of polys || []) for (const [px, py] of pts) {
    if (px < minx) minx = px; if (px > maxx) maxx = px; if (py < miny) miny = py; if (py > maxy) maxy = py;
  }
  if (!isFinite(minx)) return { x: 0, y: 0, minx: 0, miny: 0 };
  return { x: maxx - minx, y: maxy - miny, minx, miny };
}
const rot90 = (pts, w) => pts.map(([x, y]) => [y, w - x]);   // 90° → bbox y×x
// Dibuja polilíneas (cut=rojo, crease=ámbar) con offset en in, sobre la hoja en (x0,y0) mm. Flip Y.
function drawPolys(doc, polys, oxIn, oyIn, x0, y0, shMm, scale) {
  for (const p of polys) {
    if (p.color === 2) doc.setDrawColor(217, 119, 6); else doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(0.12);
    const pts = p.pts;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]; const b = pts[i + 1];
      doc.line(x0 + (a[0] + oxIn) * scale, y0 + shMm - (a[1] + oyIn) * scale,
        x0 + (b[0] + oxIn) * scale, y0 + shMm - (b[1] + oyIn) * scale);
    }
  }
}

// Dibuja la imposición con la FORMA REAL del DXF: 1UP tilea el blank, Nesting la impo tal cual
// (rotada si la hoja lo pide). Devuelve la Y final. Sin polys o sin hoja → no dibuja.
function drawImposition(doc, geo, polys, x, y, maxW, maxH) {
  const { sheetW, sheetH, nx, ny, deg, blankX, blankY, gap = 0, mode } = geo;
  if (!sheetW || !sheetH || !polys || !polys.length) return y;
  const scale = Math.min(maxW / sheetW, maxH / sheetH);
  const sw = sheetW * scale; const sh = sheetH * scale;

  doc.setDrawColor(120); doc.setLineWidth(0.4); doc.setFillColor(245, 247, 250);
  doc.rect(x, y, sw, sh, "FD");

  const bb = bboxOf(polys);
  const base = polys.map((p) => ({ color: p.color, pts: p.pts.map(([px, py]) => [px - bb.minx, py - bb.miny]) }));
  if (mode === 'nesting') {
    let set = base; let fw = bb.x; let fh = bb.y;
    if (bb.x > sheetW + 0.05 && bb.y <= sheetW + 0.05) { set = base.map((p) => ({ color: p.color, pts: rot90(p.pts, bb.x) })); fw = bb.y; fh = bb.x; }
    drawPolys(doc, set, Math.max(0, (sheetW - fw) / 2), Math.max(0, (sheetH - fh) / 2), x, y, sh, scale);
  } else {
    const cell = deg === 90 ? base.map((p) => ({ color: p.color, pts: rot90(p.pts, bb.x) })) : base;
    const bw = deg === 90 ? blankY : blankX; const bh = deg === 90 ? blankX : blankY;
    for (let r = 0; r < (ny || 1); r++) for (let c = 0; c < (nx || 1); c++) {
      const oxIn = c * (bw + gap); const oyInTop = r * (bh + gap);
      if (oxIn + bw > sheetW + 0.05 || oyInTop + bh > sheetH + 0.05) continue;
      drawPolys(doc, cell, oxIn, sheetH - oyInTop - bh, x, y, sh, scale);   // filas desde arriba
    }
  }
  doc.setFontSize(7); doc.setTextColor(100);
  const lbl = mode === 'nesting' ? `${n(sheetW)}×${n(sheetH)} in · ${n(geo.ups, 0)} piezas (impo real)`
    : `${n(sheetW)}×${n(sheetH)} in · ${nx}×${ny} ups${deg ? ` · ${deg}°` : ""}`;
  doc.text(lbl, x, y + sh + 4);
  doc.setTextColor(0);
  return y + sh + 8;
}

/**
 * Genera el Spec Sheet PDF. PURO respecto a red salvo el logo (best-effort).
 * @param {object} ctx  { title, refId, clientName, jobName, deliveryDate, createdBy, pieces[], output }
 *   piece: { name, materialName, quantity, ups, ruleLengthPerSheet, geometry }
 * @return {Promise<Blob|void>} blob si output==='blob', si no descarga el archivo.
 */
export async function buildSpecSheetPdf(ctx) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 14;
  let y = M;

  // ── Cabecera ──
  const logo = await loadLogo();
  if (logo) { try { doc.addImage(logo, "PNG", M, y, 28, 12); } catch (_) { /* sin logo */ } }
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("SPEC SHEET — Hoja de imposición", pageW / 2, y + 6, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(110);
  doc.text(ctx.refId || "", pageW - M, y + 6, { align: "right" });
  doc.setTextColor(0);
  y += 18;

  // ── Identificación ──
  autoTable(doc, {
    startY: y,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 1 },
    body: [
      ["Cliente", ctx.clientName || "—", "Trabajo", ctx.jobName || "—"],
      ["Entrega", ctx.deliveryDate || "—", "Preparado por", ctx.createdBy || "—"],
    ],
    columnStyles: { 0: { fontStyle: "bold", textColor: 90 }, 2: { fontStyle: "bold", textColor: 90 } },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Por pieza: tabla de geometría + dibujo ──
  (ctx.pieces || []).forEach((p, i) => {
    const g = p.geometry || null;
    const ups = (g && g.ups) || p.ups;
    const sheets = ups ? Math.ceil((Number(p.quantity) || 0) / ups) : null;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Pieza ${i + 1}${p.name ? ` — ${p.name}` : ""}`, M, y + 4);
    doc.setFont("helvetica", "normal");
    y += 7;

    const rows = [
      ["Material (hoja)", p.materialName || "—"],
      ["Cantidad", n(p.quantity, 0)],
      ["ups / hoja", n(ups, 0)],
      ["Hojas necesarias", sheets != null ? n(sheets, 0) : "—"],
      ["Rule length (in)", g ? `${n(g.cutLen)} corte + ${n(g.creaseLen)} hendido = ${n(g.ruleTotal)}` : n(p.ruleLengthPerSheet)],
    ];
    if (g && g.mode === "nesting") {
      rows.push(["Overall impo (in)", `${n(g.overallX)} × ${n(g.overallY)}`]);
      rows.push(["Hoja (in)", `${n(g.sheetW)} × ${n(g.sheetH)}${g.rail ? ` · carril ${g.rail}` : ""}`]);
    } else if (g) {
      rows.push(["Blank (in)", `${n(g.blankX)} × ${n(g.blankY)}`]);
      rows.push(["Hoja (in)", `${n(g.sheetW)} × ${n(g.sheetH)}`]);
      rows.push(["Nesting", `${g.nx}×${g.ny}${g.deg ? ` @ ${g.deg}°` : ""} · gap ${n(g.gap)} · aprov. ${n(g.yieldPct, 1)}%`]);
    }
    autoTable(doc, {
      startY: y,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 1.5 },
      columnStyles: { 0: { cellWidth: 55, textColor: 90 }, 1: { halign: "center" } },
      body: rows,
    });
    y = doc.lastAutoTable.finalY + 4;

    if (g && g.sheetW && p.polys && p.polys.length) {
      y = drawImposition(doc, g, p.polys, M, y, pageW - 2 * M, 70);
    } else {
      doc.setFontSize(8); doc.setTextColor(140);
      doc.text("(geometría manual — sin dibujo de imposición; subí el 1UP/DXF para el nesting)", M, y + 2);
      doc.setTextColor(0); y += 6;
    }
    y += 6;

    if (y > doc.internal.pageSize.getHeight() - 30 && i < ctx.pieces.length - 1) { doc.addPage(); y = M; }
  });

  // ── Pie ──
  doc.setFontSize(7); doc.setTextColor(140);
  doc.text("Generado por el motor de imposición de CARTONBAR — métricas validadas vs ArtiosCAD.", M, doc.internal.pageSize.getHeight() - 8);
  doc.setTextColor(0);

  const filename = `SPEC-SHEET_${(ctx.refId || "cotizacion").replace(/[^\w-]/g, "")}.pdf`;
  if (ctx.output === "blob") return doc.output("blob");
  doc.save(filename);
}
